import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../App";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { api } from "../services/apiClient";
import BackNavigationButton from "../components/navigation/BackNavigationButton";
import { useFormDraft } from "../hooks/useFormDraft";
import { useUnsavedChangesGuard } from "../hooks/useUnsavedChangesGuard";

const createInitialFormData = () => ({
  name: "",
  description: "",
  address: "",
  development_type: "Residencial",
  status: "En Construcción",
  estimated_delivery: "",
  cover_image_url: "",
  image_urls: "",
  amenities: "",
  geometry: {
    type: "Point",
    coordinates: [-99.1332, 19.4326],
  },
});

const AddDevelopment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(createInitialFormData());
  const draftKey = useMemo(
    () => `draft:add-development:${user?.id || user?.email || "anon"}`,
    [user],
  );
  const isDirty = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(createInitialFormData()),
    [formData],
  );
  const { clearDraft } = useFormDraft({
    draftKey,
    formData,
    setFormData,
    enabled: Boolean(user),
  });
  const { confirmIfDirty } = useUnsavedChangesGuard(isDirty);

  if (user?.role !== "developer" && user?.role !== "admin") {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <h2 className="text-2xl font-bold mb-4">Acceso Restringido</h2>
          <p className="text-muted-foreground mb-6">
            Solo usuarios con plan Desarrolladora pueden crear desarrollos.
          </p>
          <Button onClick={() => navigate("/pricing")}>Ver Planes</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    if (!confirmIfDirty()) return;

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/dashboard");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.address) {
      toast.error("Nombre y dirección son obligatorios");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        image_urls: formData.image_urls
          ? formData.image_urls
              .split(",")
              .map((u) => u.trim())
              .filter(Boolean)
          : [],
        amenities: formData.amenities
          ? formData.amenities
              .split(",")
              .map((a) => a.trim())
              .filter(Boolean)
          : [],
      };

      const response = await api.post("/developments", payload);

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Error al crear desarrollo");
      }

      toast.success("Desarrollo creado exitosamente");
      clearDraft();
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        <BackNavigationButton
          className="mb-6"
          dataTestId="back-btn"
          onBeforeNavigate={confirmIfDirty}
        />

        <h1
          className="text-2xl md:text-3xl font-bold mb-8"
          style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
          data-testid="add-development-title"
        >
          Nuevo Desarrollo
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información básica */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold border-b pb-2">
              Información del Desarrollo
            </h2>
            <div>
              <label className="text-sm font-medium mb-1 block">
                Nombre del Desarrollo *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Ej: Residencial Los Pinos"
                required
                data-testid="dev-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                Descripción
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Describe el desarrollo, sus características principales..."
                rows={4}
                data-testid="dev-description"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Tipo</label>
                <Select
                  value={formData.development_type}
                  onValueChange={(v) => handleChange("development_type", v)}
                >
                  <SelectTrigger data-testid="dev-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Residencial">Residencial</SelectItem>
                    <SelectItem value="Comercial">Comercial</SelectItem>
                    <SelectItem value="Mixto">Mixto</SelectItem>
                    <SelectItem value="Industrial">Industrial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Estatus
                </label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => handleChange("status", v)}
                >
                  <SelectTrigger data-testid="dev-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="En Planeación">En Planeación</SelectItem>
                    <SelectItem value="En Construcción">
                      En Construcción
                    </SelectItem>
                    <SelectItem value="Entregado">Entregado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Ubicación */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold border-b pb-2">Ubicación</h2>
            <div>
              <label className="text-sm font-medium mb-1 block">
                Dirección *
              </label>
              <AddressAutocomplete
                value={formData.address}
                onChange={(text) => handleChange("address", text)}
                onSelect={({ address, coordinates }) => {
                  setFormData((prev) => ({
                    ...prev,
                    address,
                    geometry: { type: "Point", coordinates },
                  }));
                }}
                placeholder="Buscar dirección del desarrollo..."
                required
                dataTestId="dev-address-autocomplete"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Latitud (Norte/Sur)
                </label>
                <Input
                  type="number"
                  step="any"
                  value={formData.geometry.coordinates[1]}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      geometry: {
                        ...prev.geometry,
                        coordinates: [
                          prev.geometry.coordinates[0],
                          parseFloat(e.target.value) || 0,
                        ],
                      },
                    }))
                  }
                  data-testid="dev-lat"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Longitud (Oeste/Este)
                </label>
                <Input
                  type="number"
                  step="any"
                  value={formData.geometry.coordinates[0]}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      geometry: {
                        ...prev.geometry,
                        coordinates: [
                          parseFloat(e.target.value) || 0,
                          prev.geometry.coordinates[1],
                        ],
                      },
                    }))
                  }
                  data-testid="dev-lng"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
              <strong>Cómo obtener coordenadas:</strong> Abre Google Maps, busca
              la dirección de tu desarrollo, haz click derecho en el punto
              exacto y selecciona las coordenadas que aparecen. Se copian
              automáticamente en formato: latitud, longitud. El autocompletado
              de dirección también llena las coordenadas automáticamente.
            </p>
          </div>

          {/* Detalles */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold border-b pb-2">Detalles</h2>
            <div>
              <label className="text-sm font-medium mb-1 block">
                Fecha estimada de entrega
              </label>
              <Input
                type="date"
                value={formData.estimated_delivery}
                onChange={(e) =>
                  handleChange("estimated_delivery", e.target.value)
                }
                data-testid="dev-delivery"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                Amenidades (separadas por coma)
              </label>
              <Input
                value={formData.amenities}
                onChange={(e) => handleChange("amenities", e.target.value)}
                placeholder="Alberca, Gimnasio, Jardín, Seguridad 24h"
                data-testid="dev-amenities"
              />
            </div>
          </div>

          {/* Imágenes */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold border-b pb-2">Imágenes</h2>
            <div>
              <label className="text-sm font-medium mb-1 block">
                Imagen de portada (URL)
              </label>
              <Input
                value={formData.cover_image_url}
                onChange={(e) =>
                  handleChange("cover_image_url", e.target.value)
                }
                placeholder="https://ejemplo.com/imagen-portada.jpg"
                data-testid="dev-cover"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                Galería de imágenes (URLs separadas por coma)
              </label>
              <Textarea
                value={formData.image_urls}
                onChange={(e) => handleChange("image_urls", e.target.value)}
                placeholder="https://url1.jpg, https://url2.jpg"
                rows={2}
                data-testid="dev-images"
              />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1"
              data-testid="dev-submit"
            >
              {loading ? "Creando..." : "Crear Desarrollo"}
            </Button>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
};

export default AddDevelopment;
